import axios from 'axios';
import { getConfig } from '../../AppConfig';

const config = getConfig();

// Auth Hub calls average 600ms–1s (latency is in the upstream Eatzy API).
// Per Auth Hub team guidance: do not show errors from a timeout < 3s.
// 10s gives ample headroom while still guarding against hung connections.
const authHubAxios = axios.create({
  baseURL: config.authHubUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authHeaders = (token) => {
  if (!token) return {};
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const login = async (email, password) => {
  const { data } = await authHubAxios.post('/auth/login', { email, password });
  return data;
};

export const register = async (
  email,
  password,
  name,
  source = 'consumer-portal-web'
) => {
  const { data } = await authHubAxios.post('/auth/register', {
    email,
    password,
    name,
    source,
  });
  return data;
};

export const getProfile = async (token) => {
  const { data } = await authHubAxios.get('/auth/me', authHeaders(token));
  return data;
};

export const verifyToken = async (token) => {
  const { data } = await authHubAxios.post('/api/verify', { token });
  return data;
};

export const refreshSession = async (token) => {
  const { data } = await authHubAxios.post(
    '/auth/refresh',
    {},
    authHeaders(token)
  );
  return data;
};

export const logout = async (token) => {
  const { data } = await authHubAxios.post(
    '/auth/logout',
    {},
    authHeaders(token)
  );
  return data;
};

export const forgotPassword = async (email, callbackUrl) => {
  const body = callbackUrl ? { email, redirect_url: callbackUrl } : { email };
  const { data } = await authHubAxios.post('/auth/forgot-password', body);
  return data;
};

export const resetPassword = async (token, newPassword) => {
  const { data } = await authHubAxios.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return data;
};

export const getEatzyAccessToken = async (email, password) => {
  const { data } = await authHubAxios.post('/account/authorize', {
    username: email,
    password,
  });
  return data;
};

export const getEatzyAccessTokenBySession = async (sessionToken) => {
  const { data } = await authHubAxios.post(
    '/account/authorize',
    {},
    authHeaders(sessionToken)
  );
  return data;
};

export const linkAndAuthorize = async (sessionToken) => {
  const { data } = await authHubAxios.post(
    '/account/authorize',
    { link: true },
    authHeaders(sessionToken)
  );
  return data;
};

export const registerGuest = async (name, contactEmail) => {
  const syntheticEmail = `${crypto.randomUUID()}@guest.eatzy.com`;
  const tempPassword = crypto.randomUUID();

  const { data } = await authHubAxios.post('/auth/register-guest', {
    email: syntheticEmail,
    password: tempPassword,
    name,
    contact_email: contactEmail,
    source: 'consumer-portal-web',
  });

  return {
    ...data,
    syntheticEmail,
    tempPassword,
  };
};

export const upgradeGuestAccount = async (
  sessionToken,
  realEmail,
  newPassword
) => {
  const { data } = await authHubAxios.post(
    '/auth/upgrade-guest',
    { email: realEmail, password: newPassword },
    authHeaders(sessionToken)
  );
  return data;
};

export const refreshEatzyToken = async (eatzyRefreshToken) => {
  const { data } = await axios.post(
    `${config.apiBaseUrl}/account/refresh`,
    { token: eatzyRefreshToken },
    { headers: { Authorization: `Basic ${eatzyRefreshToken}` } }
  );
  return data;
};

export const getSocialLoginUrl = (provider, callbackUrl) => {
  const encodedCallback = encodeURIComponent(
    callbackUrl || `${window.location.origin}/auth/callback`
  );
  return `${config.authHubUrl}/oauth/${provider}?callbackURL=${encodedCallback}`;
};

export default {
  login,
  register,
  registerGuest,
  getProfile,
  verifyToken,
  refreshSession,
  logout,
  forgotPassword,
  resetPassword,
  getEatzyAccessToken,
  getEatzyAccessTokenBySession,
  linkAndAuthorize,
  getSocialLoginUrl,
  upgradeGuestAccount,
  refreshEatzyToken,
};
